import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import {
  ChevronDown,
  CalendarClock,
  ExternalLink,
  FolderKanban,
  Globe2,
  Link2,
  Loader2,
  Search,
  ShieldAlert,
  ShoppingBag,
  Unplug,
} from "lucide-react";
import { StudioSidebar } from "@/app/StudioSidebar";
import { ToastMessage } from "@/app/toast";
import { StudioLoading } from "@/app/feedback";
import {
  STUDIO_DOMAINS_PATH,
  STUDIO_PROJECTS_PATH,
} from "@/app/navigation";
import {
  STUDIO_CONTENT_SHELL_CLASS,
  STUDIO_HEADER_CLASS,
  STUDIO_MAIN_CLASS,
  STUDIO_PANEL_CLASS,
  STUDIO_SECONDARY_BUTTON_CLASS,
  STUDIO_SECTION_CLASS,
  STUDIO_TITLE_CLASS,
} from "@/app/ui";
import {
  getCachedProjects,
  getCachedPublicSlots,
  listProjects,
  listPublicSlots,
  switchPublicSlot,
  createDomainRenewalPayment,
  getDomainRenewalEligibility,
  type AccountProfile,
  type ProjectSummary,
  type PublicSlot,
} from "@/lib/api";
import { displayHostname } from "@qingnest/shared/config/domain";

function formatRemainingTime(expiresAt: string) {
  const remainingMs = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  if (remainingMs <= 86_400_000) {
    const hours = Math.max(0, Math.round(remainingMs / 3_600_000));
    return { label: `剩余 ${hours} 小时`, urgent: true };
  }
  const days = Math.max(1, Math.round(remainingMs / 86_400_000));
  return { label: `剩余 ${days} 天`, urgent: false };
}

export function DomainsPage({
  account,
  authReady,
  onNavigate,
  session,
}: {
  account: AccountProfile | null;
  authReady: boolean;
  onNavigate: (path: string) => void;
  session: Session | null;
}) {
  const cachedProjects = getCachedProjects();
  const cachedSlots = getCachedPublicSlots();
  const [projects, setProjects] = useState<ProjectSummary[]>(
    cachedProjects ?? [],
  );
  const [slots, setSlots] = useState<PublicSlot[]>(cachedSlots ?? []);
  const [loading, setLoading] = useState(!cachedProjects || !cachedSlots);
  const [error, setError] = useState<string | null>(null);
  const [bindingSlotId, setBindingSlotId] = useState<string | null>(null);
  const [editingSlot, setEditingSlot] = useState<PublicSlot | null>(null);
  const [pendingBinding, setPendingBinding] = useState<{
    slot: PublicSlot;
    siteId: string | null;
  } | null>(null);
  const [domainQuery, setDomainQuery] = useState("");
  const [suffixFilter, setSuffixFilter] = useState("all");
  const [renewingSlot, setRenewingSlot] = useState<PublicSlot | null>(null);
  const [renewalDurations, setRenewalDurations] = useState<Array<1 | 3 | 6 | 12>>([]);
  const [renewalDuration, setRenewalDuration] = useState<1 | 3 | 6 | 12>(1);
  const [renewalBusy, setRenewalBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    Promise.all([listProjects(), listPublicSlots()])
      .then(([nextProjects, nextSlots]) => {
        setProjects(nextProjects);
        setSlots(nextSlots);
      })
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : "域名加载失败"),
      )
      .finally(() => setLoading(false));
  }, [session]);

  if (!authReady || (session && loading && slots.length === 0))
    return (
      <StudioLoading
        account={account}
        active="domain-management"
        label="正在读取域名"
        onNavigate={onNavigate}
      />
    );

  const projectsById = new Map(
    projects.map((project) => [project.id, project]),
  );
  const boundProjectIds = new Set(
    slots.flatMap((slot) => (slot.siteId ? [slot.siteId] : [])),
  );
  const bindableProjects = projects.filter(
    (project) =>
      !boundProjectIds.has(project.id) && project.status === "active",
  );
  const suffixes = Array.from(new Set(slots.map((slot) => displayHostname(slot.hostname).split(".").at(-1) ?? ""))).filter(Boolean).sort((a, b) => a.localeCompare(b, "zh-CN"));
  const filteredSlots = slots.filter((slot) => {
    const display = displayHostname(slot.hostname);
    const query = domainQuery.trim().toLocaleLowerCase();
    return (!query || display.toLocaleLowerCase().includes(query) || slot.hostname.toLowerCase().includes(query)) && (suffixFilter === "all" || display.split(".").at(-1) === suffixFilter);
  });

  async function changeBinding(slot: PublicSlot, siteId: string | null) {
    setBindingSlotId(slot.id);
    setError(null);
    try {
      const updatedSlot = await switchPublicSlot(slot.id, siteId);
      setSlots((current) =>
        current.map((item) => (item.id === slot.id ? updatedSlot : item)),
      );
      setEditingSlot(null);
      setPendingBinding(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "域名绑定失败");
    } finally {
      setBindingSlotId(null);
    }
  }

  async function beginRenewal(slot: PublicSlot) {
    setRenewalBusy(slot.id);
    setError(null);
    try {
      const eligibility = await getDomainRenewalEligibility(slot.id);
      if (!eligibility.eligible || !eligibility.allowedDurations.length) {
        setError(eligibility.reason || "当前不能续费");
        return;
      }
      setRenewalDurations(eligibility.allowedDurations);
      setRenewalDuration(eligibility.allowedDurations[0]);
      setRenewingSlot(slot);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "续费资格检查失败");
    } finally {
      setRenewalBusy(null);
    }
  }

  async function payRenewal() {
    if (!renewingSlot) return;
    setRenewalBusy(renewingSlot.id);
    try {
      await createDomainRenewalPayment(renewingSlot.id, renewalDuration);
      setSlots(await listPublicSlots());
      setRenewingSlot(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "余额续费失败");
      setRenewalBusy(null);
      setRenewingSlot(null);
    }
  }

  return (
    <div className="min-h-dvh bg-black">
      <section className={STUDIO_SECTION_CLASS}>
        <div className={STUDIO_CONTENT_SHELL_CLASS}>
          <StudioSidebar
            account={account}
            active="domain-management"
            onNavigate={onNavigate}
          />
          <div className={STUDIO_MAIN_CLASS}>
            <div className={STUDIO_HEADER_CLASS}>
              <div>
                <h1 className={STUDIO_TITLE_CLASS}>我的域名</h1>
                <p className="mt-2 text-sm text-zinc-500">
                  管理已租赁和平台赠送的域名。绑定或换绑后 24 小时内不能再次操作；解绑后可立即绑定其他项目。
                </p>
              </div>
              <button
                className={STUDIO_SECONDARY_BUTTON_CLASS}
                onClick={() => onNavigate(STUDIO_DOMAINS_PATH)}
                type="button"
              >
                <ShoppingBag className="h-4 w-4" />
                购买平台域名
              </button>
            </div>
            <ToastMessage message={error} />
            {slots.length === 0 ? (
              <div
                className={`${STUDIO_PANEL_CLASS} mt-5 flex min-h-64 flex-col items-center justify-center p-8 text-center`}
              >
                <Globe2 className="h-7 w-7 text-zinc-500" />
                <h2 className="mt-4 text-base font-semibold">还没有平台地址</h2>
                <p className="mt-2 max-w-md text-sm leading-6 text-zinc-500">
                  购买平台地址后，可以在这里直接选择项目进行绑定。
                </p>
                <button
                  className={`${STUDIO_SECONDARY_BUTTON_CLASS} mt-5`}
                  onClick={() => onNavigate(STUDIO_DOMAINS_PATH)}
                  type="button"
                >
                  <ShoppingBag className="h-4 w-4" />
                  查看平台地址方案
                </button>
              </div>
            ) : (
              <div className="mt-5">
                <div className="mb-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_10rem]">
                  <label className="flex h-10 items-center gap-2 rounded-md border border-white/15 px-3 focus-within:border-white/35">
                    <Search className="h-4 w-4 shrink-0 text-zinc-500" />
                    <span className="sr-only">搜索我的域名</span>
                    <input className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-zinc-700" onChange={(event) => setDomainQuery(event.target.value)} placeholder="搜索域名" value={domainQuery} />
                  </label>
                  <select aria-label="筛选域名后缀" className="h-10 cursor-pointer rounded-md border border-white/15 bg-black px-3 text-sm text-zinc-200 outline-none focus:border-white/35" onChange={(event) => setSuffixFilter(event.target.value)} value={suffixFilter}>
                    <option value="all">全部后缀</option>
                    {suffixes.map((suffix) => <option key={suffix} value={suffix}>.{suffix}</option>)}
                  </select>
                </div>
                <div className={`${STUDIO_PANEL_CLASS} overflow-hidden`}>
                {filteredSlots.length ? filteredSlots.map((slot) => {
                  const project = slot.siteId
                    ? projectsById.get(slot.siteId)
                    : null;
                  const bindingBlocked = slot.status === "blocked" || project?.status === "blocked";
                  return (
                    <div
                      className="grid gap-4 border-b border-white/10 p-4 last:border-0 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_15rem] md:items-center"
                      key={slot.id}
                    >
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 truncate text-sm font-semibold text-white">
                          <Globe2 className="h-4 w-4 shrink-0 text-zinc-400" />
                          {displayHostname(slot.hostname)}
                        </p>
                        <p className="mt-1 text-xs text-zinc-500">
                          {slot.type === "custom_domain"
                            ? "自定义域名"
                            : "平台域名"}
                        </p>
                        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-zinc-600">
                          {slot.graceExpiresAt ? (
                            <span>保留至 {new Date(slot.graceExpiresAt).toLocaleString("zh-CN")}</span>
                          ) : slot.entitlementSource === "plan_grant" ? (
                            <span>套餐赠送 · 长期有效</span>
                          ) : (
                            <><span>有效期至 {new Date(slot.expiresAt).toLocaleDateString("zh-CN")}</span>{(() => { const remaining = formatRemainingTime(slot.expiresAt); return <span className={`rounded bg-white/[0.06] px-1.5 py-0.5 ${remaining.urgent ? "text-red-400" : "text-zinc-500"}`}>{remaining.label}</span>; })()}</>
                          )}
                        </p>
                      </div>
                      <div className="grid min-w-0 grid-rows-[1rem_2.25rem] gap-1">
                        <p className="text-xs text-zinc-600">绑定项目</p>
                        {project ? (
                          <button
                            className="flex h-9 max-w-full cursor-pointer items-center gap-2 text-left text-sm text-zinc-200 hover:text-white"
                            onClick={() =>
                              onNavigate(
                                `${STUDIO_PROJECTS_PATH}/${project.id}`,
                              )
                            }
                            type="button"
                          >
                            <FolderKanban className="h-4 w-4 shrink-0" />
                            <span className="truncate">{project.name}</span>
                          </button>
                        ) : (
                          <div className="flex h-9 min-w-0 items-center gap-2 text-zinc-200 hover:text-white">
                            {bindingSlotId === slot.id ? (
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-zinc-400" />
                            ) : (
                              <Link2 className="h-4 w-4 shrink-0 text-zinc-500" />
                            )}
                            <div className="relative min-w-0 max-w-full">
                              <select
                                aria-label={`为 ${slot.hostname} 选择绑定项目`}
                                className="h-9 max-w-full cursor-pointer appearance-none bg-transparent py-0 pl-0 pr-6 text-sm text-current outline-none disabled:cursor-not-allowed disabled:text-zinc-600"
                                disabled={
                                  bindingSlotId !== null ||
                                  bindingBlocked ||
                                  bindableProjects.length === 0
                                }
                                onChange={(event) => {
                                  const siteId = event.target.value;
                                  if (siteId)
                                    setPendingBinding({ slot, siteId });
                                }}
                                value=""
                              >
                                <option
                                  className="bg-zinc-950 text-zinc-500"
                                  value=""
                                >
                                  {bindingBlocked
                                    ? "该域名已封禁，暂不能绑定"
                                    : bindableProjects.length > 0
                                      ? "选择未绑定项目"
                                      : "暂无可绑定项目"}
                                </option>
                                {bindableProjects.map((candidate) => (
                                  <option
                                    className="bg-zinc-950 text-zinc-200"
                                    key={candidate.id}
                                    value={candidate.id}
                                  >
                                    {candidate.name}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 md:justify-end">
                        {slot.type === "platform_subdomain" && slot.entitlementSource === "paid_rental" ? <button className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-white/15 px-3 text-sm text-zinc-300 hover:bg-white/5 disabled:cursor-wait disabled:opacity-50" disabled={renewalBusy !== null || slot.status === "blocked"} onClick={() => void beginRenewal(slot)} type="button">{renewalBusy === slot.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}续费</button> : null}
                        {!bindingBlocked ? <a
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-white/15 px-3 text-sm text-zinc-300 hover:bg-white/5"
                          href={slot.publicUrl}
                          rel="noreferrer"
                          target="_blank"
                        >
                          访问网站
                          <ExternalLink className="h-4 w-4" />
                        </a> : <span className="inline-flex h-9 items-center gap-2 text-xs text-red-300"><ShieldAlert className="h-4 w-4" />{project?.status === "blocked" ? "项目已封禁" : "域名已封禁"}</span>}
                        {project && !bindingBlocked ? (
                          <button
                            className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md bg-white px-3 text-sm font-semibold text-black"
                            onClick={() => setEditingSlot(slot)}
                            type="button"
                          >
                            <Link2 className="h-4 w-4" />
                            换绑 / 解绑
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                }) : <div className="px-4 py-12 text-center text-sm text-zinc-500">没有符合条件的域名</div>}
                </div>
              </div>
            )}
            {renewingSlot ? <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true"><div className="w-full max-w-md rounded-md border border-white/20 bg-black p-5"><h2 className="text-base font-semibold text-white">续费平台域名</h2><p className="mt-2 break-all text-sm text-zinc-400">{displayHostname(renewingSlot.hostname)}</p><p className="mt-3 text-sm leading-6 text-zinc-500">只可在后台设置的到期窗口内续费，并受最长提前持有期限限制。确认后从账户余额扣款。</p><div className="mt-5 grid grid-cols-4 gap-2">{renewalDurations.map((months) => <button aria-pressed={renewalDuration === months} className={`h-10 rounded-md border text-sm ${renewalDuration === months ? "border-white bg-white text-black" : "border-white/15 text-zinc-300"}`} key={months} onClick={() => setRenewalDuration(months)} type="button">{months} 个月</button>)}</div><div className="mt-6 flex justify-end gap-2"><button className="h-9 rounded-md border border-white/15 px-3 text-sm text-zinc-300" disabled={renewalBusy !== null} onClick={() => setRenewingSlot(null)} type="button">取消</button><button className="inline-flex h-9 items-center gap-2 rounded-md bg-white px-3 text-sm font-semibold text-black disabled:opacity-50" disabled={renewalBusy !== null} onClick={() => void payRenewal()} type="button">{renewalBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}余额续费</button></div></div></div> : null}
            {editingSlot && !pendingBinding ? (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
                onMouseDown={(event) => { if (event.target === event.currentTarget && !bindingSlotId) setEditingSlot(null); }}
                role="dialog"
                aria-modal="true"
              >
                <div className="w-full max-w-md rounded-md border border-white/20 bg-black p-5">
                  <h2 className="text-base font-semibold text-white">
                    管理绑定
                  </h2>
                  <p className="mt-2 break-all text-sm text-zinc-400">
                    {displayHostname(editingSlot.hostname)}
                  </p>
                  <p className="mt-3 text-sm leading-6 text-zinc-500">
                    选择操作后还需要再次确认。绑定或换绑后需等待 24
                    小时才能再次操作；解绑后可立即绑定其他项目。
                  </p>
                  <select
                    aria-label="选择新的绑定项目"
                    className="mt-4 h-10 w-full rounded-md border border-white/15 bg-black px-3 text-sm text-zinc-200 outline-none"
                    disabled={bindingSlotId !== null}
                    onChange={(event) => {
                      const siteId = event.target.value;
                      if (siteId)
                        setPendingBinding({ slot: editingSlot, siteId });
                    }}
                    value=""
                  >
                    <option value="">选择未绑定项目</option>
                    {bindableProjects.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name}
                      </option>
                    ))}
                  </select>
                  <div className="mt-5 flex flex-wrap justify-between gap-2">
                    <button
                      className="inline-flex h-9 items-center gap-2 rounded-md border border-white/15 px-3 text-sm text-zinc-400 hover:bg-white/5"
                      disabled={bindingSlotId !== null}
                      onClick={() =>
                        setPendingBinding({ slot: editingSlot, siteId: null })
                      }
                      type="button"
                    >
                      <Unplug className="h-4 w-4" />
                      解绑当前项目
                    </button>
                    <button
                      className="h-9 rounded-md border border-white/15 px-3 text-sm text-zinc-300 hover:bg-white/5"
                      onClick={() => setEditingSlot(null)}
                      type="button"
                    >
                      取消
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            {pendingBinding ? (
              <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
                onMouseDown={(event) => { if (event.target === event.currentTarget && !bindingSlotId) setPendingBinding(null); }}
                role="alertdialog"
                aria-modal="true"
              >
                <div className="w-full max-w-md rounded-md border border-white/20 bg-black p-5">
                  <h2 className="text-base font-semibold text-white">
                    确认
                    {pendingBinding.siteId
                      ? pendingBinding.slot.siteId
                        ? "换绑"
                        : "绑定"
                      : "解绑"}
                  </h2>
                  <dl className="mt-4 grid gap-3 rounded-md border border-white/10 p-4 text-sm">
                    <div>
                      <dt className="text-xs text-zinc-600">平台地址</dt>
                      <dd className="mt-1 break-all text-zinc-200">
                        {displayHostname(pendingBinding.slot.hostname)}
                      </dd>
                    </div>
                    {pendingBinding.slot.siteId ? (
                      <div>
                        <dt className="text-xs text-zinc-600">当前项目</dt>
                        <dd className="mt-1 text-zinc-200">
                          {projectsById.get(pendingBinding.slot.siteId)?.name ??
                            "未知项目"}
                        </dd>
                      </div>
                    ) : null}
                    <div>
                      <dt className="text-xs text-zinc-600">
                        {pendingBinding.siteId ? "目标项目" : "操作结果"}
                      </dt>
                      <dd className="mt-1 text-zinc-200">
                        {pendingBinding.siteId
                          ? (projectsById.get(pendingBinding.siteId)?.name ??
                            "未知项目")
                          : "平台地址将暂时无法访问"}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-4 text-sm leading-6 text-zinc-500">
                    {pendingBinding.siteId
                      ? "确认后会立即生效，之后 24 小时内不能再次换绑或解绑。"
                      : "确认后会立即生效，且可立即将该地址绑定到其他项目。"}
                  </p>
                  <div className="mt-5 flex justify-end gap-2">
                    <button
                      className="h-9 rounded-md border border-white/15 px-3 text-sm text-zinc-300"
                      disabled={bindingSlotId !== null}
                      onClick={() => setPendingBinding(null)}
                      type="button"
                    >
                      返回
                    </button>
                    <button
                      className="inline-flex h-9 items-center gap-2 rounded-md bg-white px-3 text-sm font-semibold text-black disabled:opacity-50"
                      disabled={bindingSlotId !== null}
                      onClick={() =>
                        void changeBinding(
                          pendingBinding.slot,
                          pendingBinding.siteId,
                        )
                      }
                      type="button"
                    >
                      {bindingSlotId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Link2 className="h-4 w-4" />
                      )}
                      确认操作
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
